// Based on https://github.com/afconsult/quill-mention/

import KEY_CODES from '@dui/helpers/key-codes';
import Quill from '../quill';

import { whenElementIsVisible } from '@dui/helpers/util-funcs';

export default class Mention {
  constructor(quill, options) {
    const DEFAULT_OPTIONS = {
      source: null,
      renderItemInsideElement(element, item) {
        const img = document.createElement('img');
        img.src = item.avatar_url;
        const name = document.createElement('span');
        name.innerText = item.name;
        element.appendChild(img);
        element.appendChild(name);
      },
      onSelect(item, insertItem) {
        insertItem(item);
      },
      mentionDenotationChars: [ '@' ],
      showDenotationChar: true,
      allowedChars: /^[a-zA-Z0-9_]*$/,
      minChars: 0,
      maxChars: 31,
      offsetTop: 2,
      offsetLeft: 0,
      isolateCharacter: false,
      fixMentionsToQuill: false,
      defaultMenuOrientation: 'bottom',
      dataAttributes: [
        'id',
        'name',
      ],
      onOpen() {
        return true;
      },
      onClose() {
        return true;
      },
      // Style options
      listItemClass: 'ql-mention-list-item',
      mentionContainerClass: 'ql-mention-list-container',
      mentionListClass: 'ql-mention-list',
      spaceAfterInsert: true,
    };

    this.cursorPos = null;
    this.disableLoadMore = null;
    this.isOpen = false;
    this.itemIndex = 0;
    this.mentionCharPos = null;
    this.nextPage = 1;
    this.prevSearch = null;
    this.suspendMouseEnter = false;
    this.values = [];

    this.quill = quill;
    this.options = DEFAULT_OPTIONS;

    Object.assign(this.options, options, {
      dataAttributes: Array.isArray(options.dataAttributes)
        ? this.options.dataAttributes.concat(options.dataAttributes)
        : this.options.dataAttributes,
    });

    this.createAndAddMentionContainer();
    this.addKeyboardBindings();

    quill.on('text-change', this.onTextChange.bind(this));
    quill.on('selection-change', this.onSelectionChange.bind(this));
  }

  appendLoadingIndicator() {
    const loadingEl = document.createElement('div');
    loadingEl.classList.add('loading-indicator');
    const spinnerEl = document.createElement('span');
    spinnerEl.classList.add('loading-spinner');
    loadingEl.appendChild(spinnerEl);
    this.mentionContainer.appendChild(loadingEl);
  }

  appendLoadMoreElement() {
    this.loadMoreEl = document.createElement('div');
    this.mentionContainer.appendChild(this.loadMoreEl);
  }

  attachDataValues(element, data) {
    const mention = element;

    Object.keys(data).forEach((key) => {
      if (this.options.dataAttributes.indexOf(key) < 0) {
        delete mention.dataset[key];
        return;
      }

      mention.dataset[key] = data[key];
    });

    return mention;
  }

  clearLoadMore() {
    if (this.disableLoadMore) {
      this.disableLoadMore();
    }
  }

  createAndAddMentionContainer() {
    this.mentionContainer = document.createElement('div');
    this.mentionContainer.className = this.options.mentionContainerClass ? this.options.mentionContainerClass : '';
    this.mentionContainer.style.cssText = 'display: none; position: absolute;';
    this.mentionContainer.onmousemove = this.onContainerMouseMove.bind(this);

    if (this.options.fixMentionsToQuill) {
      this.mentionContainer.style.width = 'auto';
    }

    this.mentionList = document.createElement('ul');
    this.mentionList.className = this.options.mentionListClass ? this.options.mentionListClass : '';
    this.mentionContainer.appendChild(this.mentionList);

    this.appendLoadingIndicator();
    this.appendLoadMoreElement();

    this.quill.container.appendChild(this.mentionContainer);
  }

  containerBottomIsNotVisible(topPos, containerPos) {
    const mentionContainerBottom = topPos + this.mentionContainer.offsetHeight + containerPos.top;
    return mentionContainerBottom > window.pageYOffset + window.innerHeight;
  }

  containerRightIsNotVisible(leftPos, containerPos) {
    if (this.options.fixMentionsToQuill) {
      return false;
    }

    const rightPos = leftPos + this.mentionContainer.offsetWidth + containerPos.left;
    const browserWidth = window.pageXOffset + document.documentElement.clientWidth;
    return rightPos > browserWidth;
  }

  deselectMentionListChildren() {
    const selectedMentionChildren = this.mentionList.querySelectorAll('.selected');
    [].forEach.call(selectedMentionChildren, (childNode) => {
      childNode.classList.remove('selected');
    });
  }

  destroy() {
    this.clearLoadMore();
  }

  enableLoadMore() {
    this.clearLoadMore();
    this.disableLoadMore = whenElementIsVisible(this.loadMoreEl, this.onSomethingChange.bind(this), this.mentionContainer);
  }

  getItemData() {
    return this.mentionList.childNodes[this.itemIndex].dataset;
  }

  showMentionList() {
    this.mentionContainer.style.visibility = 'hidden';
    this.mentionContainer.style.display = '';
    this.setMentionContainerPosition();
    this.setIsOpen(true);
  }

  hideMentionList() {
    this.mentionContainer.style.display = 'none';
    this.resetMentionList();
    this.setIsOpen(false);
  }

  hasValidChars(s) {
    return this.options.allowedChars.test(s);
  }

  highlightItem(scrollItemInView = true) {
    this.deselectMentionListChildren();
    this.mentionList.childNodes[this.itemIndex].classList.add('selected');

    if (this.nextPage && this.itemIndex === (this.values.length - 1)) {
      this.onSomethingChange();
    }

    if (!scrollItemInView) {
      return;
    }

    const itemHeight = this.mentionList.childNodes[this.itemIndex].offsetHeight;
    const itemPos = this.itemIndex * itemHeight;
    const containerTop = this.mentionContainer.scrollTop;
    const containerBottom = containerTop + this.mentionContainer.offsetHeight;

    if (itemPos < containerTop) {
      // Scroll up if the item is above the top of the container
      this.mentionContainer.scrollTop = itemPos;
      return;
    }

    if (itemPos > (containerBottom - itemHeight)) {
      // Scroll down if any part of the element is below the bottom of the container
      this.mentionContainer.scrollTop += (itemPos - containerBottom) + itemHeight;
    }
  }

  insertItem(itemData) {
    if (!itemData) {
      return;
    }

    if (!this.options.showDenotationChar) {
      itemData.denotationChar = '';
    }

    const prevMentionCharPos = this.mentionCharPos;

    this.quill
      .deleteText(this.mentionCharPos, this.cursorPos - this.mentionCharPos, Quill.sources.USER);

    this.quill.insertEmbed(prevMentionCharPos, 'mention', itemData, Quill.sources.USER);


    let targetSelectionIndex = prevMentionCharPos + 1;

    if (this.options.spaceAfterInsert) {
      this.quill.insertText(prevMentionCharPos + 1, ' ', Quill.sources.USER);
      targetSelectionIndex = prevMentionCharPos + 2;
    }

    this.quill.setSelection(targetSelectionIndex, Quill.sources.USER);
    this.hideMentionList();
  }

  nextItem() {
    this.itemIndex = (this.itemIndex + 1) % this.values.length;
    this.suspendMouseEnter = true;
    this.highlightItem();
  }

  onContainerMouseMove() {
    this.suspendMouseEnter = false;
  }

  onItemClick(e) {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.itemIndex = e.currentTarget.dataset.index;
    this.highlightItem();
    this.selectItem();
  }

  onItemMouseEnter(e) {
    if (this.suspendMouseEnter) {
      return;
    }

    const index = Number(e.target.dataset.index);

    if (Number.isNaN(index) || index === this.itemIndex) {
      return;
    }

    this.itemIndex = index;
    this.highlightItem(false);
  }

  onSelectionChange(range) {
    if (range && range.length === 0) {
      this.onSomethingChange();
      return;
    }

    this.hideMentionList();
  }

  onSomethingChange() {
    const range = this.quill.getSelection();
    if (range == null) {
      return;
    }

    this.cursorPos = range.index;
    const startPos = Math.max(0, this.cursorPos - this.options.maxChars);
    const beforeCursorPos = this.quill.getText(startPos, this.cursorPos - startPos);

    const mentionCharIndex = this.options.mentionDenotationChars.reduce((prev, cur) => {
      const previousIndex = prev;
      const mentionIndex = beforeCursorPos.lastIndexOf(cur);
      return mentionIndex > previousIndex ? mentionIndex : previousIndex;
    }, -1);

    if (mentionCharIndex < 0) {
      this.hideMentionList();
      return;
    }

    if (this.options.isolateCharacter &&
      !(mentionCharIndex === 0 || !!beforeCursorPos[mentionCharIndex - 1].match(/\s/g))) {
      this.hideMentionList();
      return;
    }

    const mentionCharPos = this.cursorPos - (beforeCursorPos.length - mentionCharIndex);
    this.mentionCharPos = mentionCharPos;
    const textAfter = beforeCursorPos.substring(mentionCharIndex + 1);

    if ((textAfter.length >= this.options.minChars) && this.hasValidChars(textAfter)) {
      const mentionChar = beforeCursorPos[mentionCharIndex];
      this.setLoadingState(true);

      if ((textAfter !== this.prevSearch) || (this.nextPage === 1)) {
        this.resetMentionList(true);
        this.prevSearch = textAfter;
      }

      this.options.source(textAfter, this.nextPage, this.renderList.bind(this, mentionChar), mentionChar);
      return;
    }

    this.hideMentionList();
  }

  onTextChange(delta, oldDelta, source) {
    if (source !== Quill.sources.USER) {
      return;
    }

    this.onSomethingChange();
  }

  prevItem() {
    this.itemIndex = ((this.itemIndex + this.values.length) - 1) % this.values.length;
    this.suspendMouseEnter = true;
    this.highlightItem();
  }

  renderList(mentionChar, data, meta) {
    this.setLoadingState(false);

    if (!data || !data.length) {
      this.hideMentionList();
      return;
    }

    this.nextPage = meta.next_page;
    const idxOffset = this.values.length;
    this.values = this.values.concat(data);

    if (meta.current_page === 1) {
      this.mentionList.innerHTML = '';
      this.itemIndex = 0;
    }

    for (let i = 0; i < data.length; i += 1) {
      this.renderListItem(data[i], i, idxOffset, mentionChar);
    }

    this.setLoadMoreElement();

    if (meta.current_page === 1) {
      this.highlightItem();
    }

    this.showMentionList();
  }

  renderListItem(itemData, idx, idxOffset, mentionChar) {
    const li = document.createElement('li');
    this.options.renderItemInsideElement(li, itemData);
    li.className = this.options.listItemClass ? this.options.listItemClass : '';
    li.dataset.denotationChar = mentionChar;
    li.dataset.index = (idx + idxOffset);
    li.onclick = this.onItemClick.bind(this);
    li.onmouseenter = this.onItemMouseEnter.bind(this);
    li.title = itemData.name;
    this.mentionList.appendChild(this.attachDataValues(li, itemData));
  }

  resetMentionList(clearContent = false) {
    this.nextPage = 1;
    this.values = [];

    if (!clearContent) {
      return;
    }

    this.mentionList.innerHTML = '';
  }

  selectItem() {
    const itemdData = this.getItemData();
    this.options.onSelect(itemdData, (asyncData) => {
      this.insertItem(asyncData);
    });
    this.hideMentionList();
  }

  setIsOpen(isOpen) {
    if (this.isOpen === isOpen) {
      return;
    }

    if (isOpen) {
      this.options.onOpen();
    } else {
      this.options.onClose();
    }

    this.isOpen = isOpen;
  }

  setLoadMoreElement() {
    if (!this.nextPage) {
      return;
    }

    this.enableLoadMore();
  }

  setLoadingState(isLoading) {
    this.mentionContainer.classList.toggle('loading', isLoading);

    if (this.isOpen) {
      return;
    }

    this.showMentionList();
  }

  setMentionContainerPosition() {
    const containerPos = this.quill.container.getBoundingClientRect();
    const mentionCharPos = this.quill.getBounds(this.mentionCharPos);
    const containerHeight = this.mentionContainer.offsetHeight;

    let topPos = this.options.offsetTop;
    let leftPos = this.options.offsetLeft;

    // handle horizontal positioning
    if (this.options.fixMentionsToQuill) {
      const rightPos = 0;
      this.mentionContainer.style.right = `${rightPos}px`;
    } else {
      leftPos += mentionCharPos.left;
    }

    if (this.containerRightIsNotVisible(leftPos, containerPos)) {
      const containerWidth = this.mentionContainer.offsetWidth + this.options.offsetLeft;
      const quillWidth = containerPos.width;
      leftPos = quillWidth - containerWidth;
    }

    // handle vertical positioning
    if (this.options.defaultMenuOrientation === 'top') {
      // Attempt to align the mention container with the top of the quill editor
      if (this.options.fixMentionsToQuill) {
        topPos = -1 * (containerHeight + this.options.offsetTop);
      } else {
        topPos = mentionCharPos.top - (containerHeight + this.options.offsetTop);
      }

      // default to bottom if the top is not visible
      if (topPos + containerPos.top <= 0) {
        let overMentionCharPos = this.options.offsetTop;

        if (this.options.fixMentionsToQuill) {
          overMentionCharPos += containerPos.height;
        } else {
          overMentionCharPos += mentionCharPos.bottom;
        }

        topPos = overMentionCharPos;
      }
    } else {
      // Attempt to align the mention container with the bottom of the quill editor
      if (this.options.fixMentionsToQuill) {
        topPos += containerPos.height;
      } else {
        topPos += mentionCharPos.bottom;
      }

      // default to the top if the bottom is not visible
      if (this.containerBottomIsNotVisible(topPos, containerPos)) {
        let overMentionCharPos = this.options.offsetTop * -1;

        if (!this.options.fixMentionsToQuill) {
          overMentionCharPos += mentionCharPos.top;
        }

        topPos = overMentionCharPos - containerHeight;
      }
    }

    if (topPos >= 0) {
      this.mentionContainer.classList.add(`${this.options.mentionContainerClass}-bottom`);
      this.mentionContainer.classList.remove(`${this.options.mentionContainerClass}-top`);
    } else {
      this.mentionContainer.classList.add(`${this.options.mentionContainerClass}-top`);
      this.mentionContainer.classList.remove(`${this.options.mentionContainerClass}-bottom`);
    }

    this.mentionContainer.style.top = `${topPos}px`;
    this.mentionContainer.style.left = `${leftPos}px`;

    this.mentionContainer.style.visibility = 'visible';
  }

  // Keyboard bindings
  addKeyboardBindings() {
    this.quill.keyboard.addBinding({
      key: KEY_CODES.TAB,
    }, this.selectHandler.bind(this));
    this.quill.keyboard.bindings[KEY_CODES.TAB].unshift(this.quill.keyboard.bindings[KEY_CODES.TAB].pop());

    this.quill.keyboard.addBinding({
      key: KEY_CODES.RETURN,
    }, this.selectHandler.bind(this));
    this.quill.keyboard.bindings[KEY_CODES.RETURN].unshift(this.quill.keyboard.bindings[KEY_CODES.RETURN].pop());

    this.quill.keyboard.addBinding({
      key: KEY_CODES.ESCAPE,
    }, this.escapeHandler.bind(this));

    this.quill.keyboard.addBinding({
      key: KEY_CODES.UP_ARROW,
    }, this.upHandler.bind(this));

    this.quill.keyboard.addBinding({
      key: KEY_CODES.DOWN_ARROW,
    }, this.downHandler.bind(this));
  }

  downHandler() {
    if (this.isOpen) {
      this.nextItem();
      return false;
    }
    return true;
  }

  escapeHandler() {
    if (this.isOpen) {
      this.hideMentionList();
      return false;
    }
    return true;
  }

  selectHandler() {
    if (this.isOpen) {
      this.selectItem();
      return false;
    }
    return true;
  }

  upHandler() {
    if (this.isOpen) {
      this.prevItem();
      return false;
    }
    return true;
  }
  // END Keyboard bindings
}
